// src/components/Sucursales/SucursalesIndex.tsx

import React, { useState, useEffect, useRef } from "react";
// CAMBIO: La ruta de importación ahora coincide con la nueva ubicación del archivo
import * as S from "./SucursalIndex.styles";

// Importaciones de DevExtreme
import DataGrid, {
  Column,
  HeaderFilter,
  Toolbar,
  Item,
  ColumnFixing,
  SearchPanel,
  FilterRow,
} from "devextreme-react/data-grid";
import { Popup } from "devextreme-react/popup";
import Form, { SimpleItem, Label, RequiredRule } from "devextreme-react/form";
import type { default as FormInstance } from "devextreme/ui/form";
import { Button } from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import { FiEdit, FiTrash2, FiCheckCircle } from "react-icons/fi";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";
import { exportDataGrid } from "devextreme/excel_exporter";
import { apiService } from "../../../services/apiService";
import axios, { AxiosError } from "axios";
import { ApiError } from "../../../services/apiError";
import { Sucursal } from "../../../dtos/Sucursal";
import { useNavigate } from "react-router-dom";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    const errorMessage =
      error.response?.data?.message || "Ocurrió un error en el servidor.";
    return {
      message: errorMessage,
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return {
    message: "Ocurrió un error inesperado. Revisa tu conexión de red.",
  };
};

const SucursalesIndex = () => {
  const dataGridRef = useRef<any>(null);
  const [dataSource, setDataSource] = useState<Sucursal[]>([]);
  const [loading, setLoading] = useState(true);
  const [isFormPopupVisible, setIsFormPopupVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<Partial<Sucursal> | null>(
    null,
  );
  const [isDeletePopupVisible, setIsDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Sucursal | null>(null);
  const navigate = useNavigate();
  const formRef = useRef<FormInstance | null>(null);

  const handleExportExcel = async () => {
    const gridInstance = dataGridRef.current?.instance();

    if (!gridInstance) {
      notify("No se pudo obtener la tabla para exportar.", "error", 3000);
      return;
    }

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Sucursales");

    await exportDataGrid({
      component: gridInstance,
      worksheet,
      autoFilterEnabled: true,

      customizeCell: ({ gridCell, excelCell }) => {
        if (gridCell?.rowType === "header") {
          excelCell.font = { bold: true };
        }
      },

      topLeftCell: { row: 1, column: 1 },

      selectedRowsOnly: false,
    });

    worksheet.eachRow((row) => {
      const lastCell = row.getCell(row.cellCount);

      if (lastCell.value === "Acciones") {
        worksheet.spliceColumns(row.cellCount, 1);
      }
    });

    worksheet.spliceColumns(4, 1);

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], { type: "application/octet-stream" }),
      "Sucursales.xlsx",
    );
  };
  const fetchTiposEnvase = async () => {
    setLoading(true);
    try {
      const response = await apiService.get<Sucursal[]>(
        "/api/Catalogos/Sucursales",
      );
      setDataSource(response);
    } catch (err: any) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTiposEnvase();
  }, []);

  const handleAdd = () => {
    setEditingItem({ estatusActivo: true });
    setIsFormPopupVisible(true);
  };

  const handleEdit = (data: Sucursal) => {
    setEditingItem({ ...data });
    setIsFormPopupVisible(true);
  };

  const handleDelete = (data: Sucursal) => {
    setItemToDelete(data);
    setIsDeletePopupVisible(true);
  };

  const confirmDelete = async () => {
    if (itemToDelete) {
      try {
        await apiService.patch(
          `/api/Catalogos/sucursales/${itemToDelete.id}/cambiastatus`,
          "",
        );
        fetchTiposEnvase();
        notify("Cambio de estatus realizado con éxito.", "success", 2000);
      } catch (error) {
        notify("Error al cambiar el estatus del registro.", "error", 3000);
      } finally {
        setIsDeletePopupVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleSave = async () => {
    const form = formRef.current;
    if (!form) return;

    const validation = await form.validate();
    if (!validation.isValid) {
      notify(
        "Por favor, complete todos los campos requeridos.",
        "warning",
        2000,
      );
      return;
    }

    const formData = form.option("formData") as Sucursal;

    try {
      if (formData.id) {
        const payload = {
          ...formData,
          usuarioDelUltimoCambio: "Sistema",
          fechaUltimoCambio: new Date().toISOString(),
        };
        const response = await apiService.put<Sucursal, Sucursal>(
          `/api/Catalogos/sucursales/${formData.id}`,
          payload,
        );
        setDataSource((prev) =>
          prev.map((item) => (item.id === formData.id ? response : item)),
        );
        notify("Sucursal actualizada con éxito.", "success", 2000);
      } else {
        const payload = {
          ...formData,
          numSucursal: Number(formData.numSucursal),
          nombreSuc: formData.nombreSuc,
        };
        const response = await apiService.post<Sucursal, Sucursal>(
          "/api/Catalogos/Sucursales",
          payload,
        );
        setDataSource((prev) => [...prev, response]);
        notify("Sucursal creada con éxito.", "success", 2000);
      }
      setIsFormPopupVisible(false);
    } catch (error) {
      notify("Error al guardar el registro.", "error", 3000);
    }
  };

  const EstatusCell = (cellInfo: any) => {
    const isActive = cellInfo.value === true;
    return (
      <div
        style={{
          padding: "4px 10px",
          borderRadius: "12px",
          fontSize: "12px",
          fontWeight: 500,
          color: "#fff",
          backgroundColor: isActive ? "#16a34a" : "#71717a",
          textAlign: "center",
        }}
      >
        {isActive ? "Activo" : "Inactivo"}
      </div>
    );
  };

  const ActionsCell = (cellData: any) => {
    const sucursal = cellData.data as Sucursal;
    const isActive = sucursal.estatusActivo;
    return (
      <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
        <Button
          hint="Editar"
          onClick={() => handleEdit(sucursal)}
          stylingMode="text"
        >
          <FiEdit size={18} color="#1d4ed8" />
        </Button>
        <Button
          hint={isActive ? "Desactivar" : "Activar"}
          onClick={() => handleDelete(sucursal)}
          stylingMode="text"
        >
          {isActive ? (
            <FiTrash2 size={18} color="#dc2626" />
          ) : (
            <FiCheckCircle size={18} color="#16a34a" />
          )}
        </Button>
      </div>
    );
  };

  return (
    <S.PageWrapper>
      {/* CAMBIO 1: Activa los estilos globales para el título */}
      <S.GlobalPopupStyles />
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "15px",
        }}
      >
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate(-1)} // ← vuelve a la pestaña anterior
        />
      </div>
      <S.Header>Catálogo de sucursales</S.Header>
      <DataGrid
        ref={dataGridRef}
        dataSource={dataSource}
        keyExpr="id"
        showBorders={true}
        columnAutoWidth={true}
        hoverStateEnabled={true}
        loadPanel={{ enabled: loading }}
      >
        <HeaderFilter visible={true} />
        <Toolbar>
          <Item name="searchPanel" />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Exportar Excel",
              icon: "xlsxfile",
              stylingMode: "contained",
              type: "default",
              onClick: handleExportExcel,
            }}
          />
          <Item
            location="after"
            widget="dxButton"
            options={{
              text: "Añadir sucursal",
              icon: "add",
              stylingMode: "contained",
              type: "default",
              onClick: handleAdd,
            }}
          />
        </Toolbar>
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder="Buscar..."
        />
        <FilterRow visible={true} />
        <HeaderFilter visible={true} allowSearch={true} />
        <ColumnFixing enabled={true} />
        <Column
          dataField="numSucursal"
          caption="CC"
          width={120}
          alignment="left"
        />
        <Column dataField="nombreSuc" caption="Nombre sucursal" width={250} />
        <Column
          dataField="estatusActivo"
          caption="Estatus"
          alignment="center"
          cellRender={EstatusCell}
          width={300}
        />
        <Column
          caption="Acciones"
          alignment="center"
          width={300}
          cellRender={ActionsCell}
        />
      </DataGrid>

      {/* --- Popup de Edición / Creación MODIFICADO --- */}
      <Popup
        visible={isFormPopupVisible}
        onHiding={() => setIsFormPopupVisible(false)}
        dragEnabled={false}
        showCloseButton={true}
        showTitle={true}
        title={
          editingItem?.id
            ? `Editar Sucursal: ${editingItem.nombreSuc}`
            : "Añadir nueva sucursal"
        }
        width={500}
        height="auto"
        className="custom-form-popup" // <-- Clase para que funcionen los estilos
        // CAMBIO 2: Se elimina por completo la propiedad 'toolbarItems'
      >
        <S.FormContainer>
          <Form
            formData={editingItem}
            onInitialized={(e) => {
              formRef.current = e.component ?? null;
            }}
          >
            <SimpleItem dataField="numSucursal">
              <Label text="Núm. Sucursal" />
              <RequiredRule message="El Núm. Sucursal es requerido." />
            </SimpleItem>
            <SimpleItem dataField="nombreSuc">
              <Label text="Nombre sucursal" />
              <RequiredRule message="El nombre de la sucursal es requerido." />
            </SimpleItem>
            <SimpleItem dataField="estatusActivo" visible={false}>
              {/* Este campo se queda oculto y funciona igual */}
            </SimpleItem>
          </Form>
        </S.FormContainer>

        {/* CAMBIO 3: Se añade el footer personalizado con nuestros botones */}
        <S.PopupFooter>
          <S.CancelButton onClick={() => setIsFormPopupVisible(false)}>
            Cancelar
          </S.CancelButton>
          <S.SaveButton onClick={handleSave}>Guardar</S.SaveButton>
        </S.PopupFooter>
      </Popup>

      {/* El popup de confirmación se queda igual */}
      <Popup
        visible={isDeletePopupVisible}
        onHiding={() => setIsDeletePopupVisible(false)}
        dragEnabled={false}
        showTitle={true}
        title={`Confirmar ${
          itemToDelete?.estatusActivo ? "Desactivación" : "Activación"
        }`}
        width={400}
        height="auto"
        toolbarItems={[
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: `Sí, ${
                itemToDelete?.estatusActivo ? "Desactivar" : "Activar"
              }`,
              type: itemToDelete?.estatusActivo ? "danger" : "success",
              stylingMode: "contained",
              onClick: confirmDelete,
            },
          },
          {
            toolbar: "bottom",
            location: "after",
            widget: "dxButton",
            options: {
              text: "Cancelar",
              stylingMode: "text",
              onClick: () => setIsDeletePopupVisible(false),
            },
          },
        ]}
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <p>
            ¿Estás seguro de que deseas{" "}
            <strong>
              {itemToDelete?.estatusActivo ? "desactivar" : "activar"}
            </strong>{" "}
            la sucursal
          </p>
          <p style={{ fontWeight: "bold", margin: "10px 0" }}>
            "{itemToDelete?.nombreSuc}"?
          </p>
        </div>
      </Popup>
    </S.PageWrapper>
  );
};

export default SucursalesIndex;
